import React from "react";
import { Box, BoxProps, Flex, Text } from "@chakra-ui/react";
import { useLandingTheme } from "../../LandingThemeContext";

// One container, one narrow measure, one section gap, one heading-to-content gap.
export const CONTAINER_WIDTH = "1120px";
export const NARROW_MEASURE = "640px";
export const SECTION_GAP = { base: "88px", lg: "120px" };
export const HEADING_TO_CONTENT_GAP = "40px";
export const PAGE_SIDE_PADDING = { base: "20px", md: "32px" };

export function scaledPixels(basePixels: number, scale: number) {
  return `${Math.round(basePixels * scale)}px`;
}

export function ThemedContainer({ children, maxW = CONTAINER_WIDTH, ...props }: BoxProps) {
  return (
    <Box w="100%" maxW={maxW} mx="auto" px={PAGE_SIDE_PADDING} {...props}>
      {children}
    </Box>
  );
}

// Small tracked label. Used only where it encodes something (the hero context line, Blueprint's figure numbers).
export function ThemedEyebrow({ children, ...props }: BoxProps) {
  const theme = useLandingTheme();
  return (
    <Text
      fontFamily={theme.font.mono}
      fontSize="13px"
      fontWeight={600}
      letterSpacing="0.1em"
      textTransform="uppercase"
      color={theme.color.muted}
      {...props}
    >
      {children}
    </Text>
  );
}

export function ThemedDisplayTitle({ children, level = "section", ...props }: BoxProps & { level?: "hero" | "section" | "sub" }) {
  const theme = useLandingTheme();
  const scale = theme.heading.sizeScale;
  const sizes = {
    hero: { base: scaledPixels(40, scale), md: scaledPixels(56, scale), lg: scaledPixels(68, scale) },
    section: { base: scaledPixels(28, scale), md: scaledPixels(40, scale), lg: scaledPixels(48, scale) },
    sub: { base: scaledPixels(22, scale), md: scaledPixels(24, scale) },
  }[level];
  return (
    <Text
      as={level === "hero" ? "h1" : "h2"}
      fontFamily={theme.font.display}
      fontWeight={theme.heading.weight}
      letterSpacing={theme.heading.letterSpacing}
      fontSize={sizes}
      lineHeight={level === "hero" ? 1.02 : 1.1}
      color={theme.color.heading}
      sx={{ textWrap: "balance" }}
      {...props}
    >
      {children}
    </Text>
  );
}

export function ThemedBody({ children, ...props }: BoxProps) {
  const theme = useLandingTheme();
  return (
    <Text fontFamily={theme.font.body} fontSize={{ base: "17px", md: "18px" }} lineHeight={1.6} color={theme.color.text} {...props}>
      {children}
    </Text>
  );
}

export function ThemedCaption({ children, ...props }: BoxProps) {
  const theme = useLandingTheme();
  return (
    <Text fontFamily={theme.font.body} fontSize="14px" lineHeight={1.5} color={theme.color.muted} {...props}>
      {children}
    </Text>
  );
}

// Blueprint numbers every section as a figure; other themes let the title stand alone.
export function FigureLabel({ figureNumber, label, ...props }: BoxProps & { figureNumber: number; label: string }) {
  const theme = useLandingTheme();
  if (!theme.useFigureLabels) return null;
  return (
    <ThemedEyebrow mb="14px" {...props}>
      Fig. {figureNumber} — {label}
    </ThemedEyebrow>
  );
}

// Title + body in a 5-column left rail, content in the remaining 7. Flip to put content on the left.
export function SplitSection({
  figureNumber,
  figureLabel,
  title,
  body,
  children,
  contentFirst = false,
  align = "flex-start",
  ...props
}: BoxProps & {
  figureNumber: number;
  figureLabel: string;
  title: React.ReactNode;
  body?: React.ReactNode;
  contentFirst?: boolean;
  align?: "flex-start" | "center";
}) {
  return (
    <Box as="section" mt={SECTION_GAP} {...props}>
      <ThemedContainer>
        <Flex direction={{ base: "column", lg: contentFirst ? "row-reverse" : "row" }} gap={{ base: "32px", lg: "64px" }} align={{ base: "stretch", lg: align }}>
          <Box flex="1" minW={0}>
            <FigureLabel figureNumber={figureNumber} label={figureLabel} />
            <ThemedDisplayTitle>{title}</ThemedDisplayTitle>
            {body && (
              <ThemedBody mt="16px" maxW="440px">
                {body}
              </ThemedBody>
            )}
          </Box>
          <Box flex="1" minW={0}>
            {children}
          </Box>
        </Flex>
      </ThemedContainer>
    </Box>
  );
}

// A hairline rule across the container — the page's divider instead of boxes.
export function Rule(props: BoxProps) {
  const theme = useLandingTheme();
  return <Box h="1px" bg={theme.color.border} {...props} />;
}

// The one surface treatment per theme: cards with the theme's border/shadow. Used sparingly.
export function ThemedPanel({ children, ...props }: BoxProps) {
  const theme = useLandingTheme();
  return (
    <Box bg={theme.color.surface} border={theme.card.border} boxShadow={theme.card.shadow} borderRadius={theme.shape.cardRadius} {...props}>
      {children}
    </Box>
  );
}
